import clsx from 'clsx';
import { memo, useCallback, useState, type Dispatch, type SetStateAction } from 'react';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Button } from '@flintmint/web-shared/components/button';
import { AudioPreviewModal } from '@/components/audio-preview-modal';

import { openSourcePreview } from '@/utils/source-preview';
import { isFileNotFoundError } from '@/services/firebase-storage-service';
import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';

import { useItemActions } from '@/hooks/use-item-actions';
import {
  getSourceId,
  useSourceListModel,
  toDeleteCandidate,
  type NoteUserSourceMap,
  type SourceSearchItem,
} from '@/hooks/use-source-list-model';
import { useUserSourceMakeGlobal } from '@/hooks/rxdb-models';
import { SourceRow } from '@/components/source-row';
import { SourceListLayout } from '../source-list-layout';

import FileGroupSvg from '@/assets/icons/file-group.svg?react';

import styles from '@/components/ai-sources-section/ai-context-sources-section.module.scss';

export const SourcePanel = (props: SourcePanelProps) => {
  const {
    activeTab,
    noteUserSources,
    toggleAttachment,
    showNoAttachedState,
    showAllUserSources,
    setCortexPreviewId,
    openAddContextItems,
    openManageContextItems,
    onDetachUserSources,
    setIsLastItemVisible,
    attachedUserSourceIds,
    onUserSourceEnabledChange,
  } = props;

  const [audioPreviewId, setAudioPreviewId] = useState<string>();

  const { configureDestructiveOptions } = useItemActions();
  const { mutate: makeUserSourceGlobal } = useUserSourceMakeGlobal();
  const { showToastNotification } = useToaster();

  const {
    searchTerm: searchValue,
    setSearchTerm: setSearchValue,
    sortedUserSources,
    isLoading,
  } = useSourceListModel({
    ids: attachedUserSourceIds,
    showAllUserSources,
    noteUserSources,
    resetSearchWhenNoAttachedState: !!showNoAttachedState,
  });

  const isAttachedTab = activeTab === 'on-this-note';
  const isLibraryTab = activeTab === 'from-source-library';

  const onDetach = useCallback((id: string) => onDetachUserSources?.([id]), [onDetachUserSources]);

  const renderRow = useCallback(
    (item: SourceSearchItem) => (
      <SourcePanelRow
        item={item}
        isAttachedTab={isAttachedTab}
        isLibraryTab={isLibraryTab}
        noteUserSources={noteUserSources}
        onDetach={onDetach}
        toggleAttachment={toggleAttachment}
        onUserSourceEnabledChange={onUserSourceEnabledChange}
        onOpenAudioPreview={setAudioPreviewId}
        setCortexPreviewId={setCortexPreviewId}
        showToastNotification={showToastNotification}
        configureDestructiveOptions={configureDestructiveOptions}
        makeUserSourceGlobal={makeUserSourceGlobal}
      />
    ),
    [
      isAttachedTab,
      isLibraryTab,
      noteUserSources,
      onDetach,
      onUserSourceEnabledChange,
      setCortexPreviewId,
      showToastNotification,
      toggleAttachment,
      configureDestructiveOptions,
      makeUserSourceGlobal,
    ]
  );

  return (
    <>
      <SourceListLayout<SourceSearchItem>
        data={sortedUserSources}
        loading={isLoading}
        getItemKey={getSourceId}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onAddContextItems={openAddContextItems}
        addActionMode="icon"
        showNoAttachedState={!!showNoAttachedState}
        defaultSize={60}
        paddingEnd={sortedUserSources.length > 2 ? 72 : 0}
        onLastItemVisible={setIsLastItemVisible}
        resetKey={`${searchValue}`}
        noAttachedConfig={{
          className: clsx(styles.noAttachedFiles, 'no-attached-files'),
          title: 'No Files Attached',
          icon: <FileGroupSvg />,
          description:
            'Add text files, markdown files, images, voice recordings and PDFs to provide AI with context to work with.',
          actionRenderer: () => (
            <Button
              appearance="outline"
              iconOnly
              cornerRadius="smooth"
              color="dark"
              onClick={openManageContextItems || openAddContextItems}
              data-testid="add-context-item-from-sidebar"
            >
              <Icon type="plus" size={20} />
            </Button>
          ),
        }}
        noResultsConfig={{
          className: styles.noData,
          icon: <Icon type="search-01-bulk" size={60} multiColor />,
          getDescription: (term) => {
            if (term) {
              return `No Sources found\n for your request`;
            }

            return isLibraryTab ? `No Sources in\n your library yet` : `No Sources attached\n to this note yet`;
          },
        }}
        rowRenderer={renderRow}
      />
      <AudioPreviewModal open={!!audioPreviewId} sourceId={audioPreviewId} onClose={() => setAudioPreviewId(undefined)} />
    </>
  );
};

interface SourcePanelProps {
  activeTab?: string;
  showAllUserSources?: boolean;
  showNoAttachedState?: boolean;
  attachedUserSourceIds: string[];
  openAddContextItems: () => void;
  openManageContextItems?: () => void;
  noteUserSources: NoteUserSourceMap;
  setIsLastItemVisible: (visible: boolean) => void;
  toggleAttachment: (sourceId: string, attached: boolean) => void;
  setCortexPreviewId: Dispatch<SetStateAction<string | undefined>>;
  onDetachUserSources?: (userSourceIds: string[]) => void | Promise<void>;
  onUserSourceEnabledChange?: (userSourceId: string, enabled: boolean) => void | Promise<void>;
}

interface SourcePanelRowProps {
  item: SourceSearchItem;
  isAttachedTab: boolean;
  isLibraryTab: boolean;
  noteUserSources: NoteUserSourceMap;
  onDetach: (id: string) => void;
  toggleAttachment: (sourceId: string, attached: boolean) => void;
  onUserSourceEnabledChange?: (userSourceId: string, enabled: boolean) => void | Promise<void>;
  onOpenAudioPreview: (id: string) => void;
  setCortexPreviewId: Dispatch<SetStateAction<string | undefined>>;
  showToastNotification: ReturnType<typeof useToaster>['showToastNotification'];
  configureDestructiveOptions: ReturnType<typeof useItemActions>['configureDestructiveOptions'];
  makeUserSourceGlobal: ReturnType<typeof useUserSourceMakeGlobal>['mutate'];
}

const SourcePanelRow = memo((props: SourcePanelRowProps) => {
  const {
    item,
    isAttachedTab,
    isLibraryTab,
    noteUserSources,
    onDetach,
    toggleAttachment,
    onUserSourceEnabledChange,
    onOpenAudioPreview,
    setCortexPreviewId,
    showToastNotification,
    configureDestructiveOptions,
    makeUserSourceGlobal,
  } = props;

  return (
    <div className={styles.contextItemRow}>
      <SourceRow
        item={item}
        appearance="compact"
        canToggleEnabled={isAttachedTab}
        canToggleAttachment={isLibraryTab}
        highlightAttached={isLibraryTab}
        noteUserSources={noteUserSources}
        onDetach={onDetach}
        toggleAttachment={toggleAttachment}
        onUserSourceEnabledChange={onUserSourceEnabledChange}
        onOpenAudioPreview={onOpenAudioPreview}
        persistedExtraProps={({ item: rowItem, sourceDoc, sourceId }) => ({
          onPreview: () =>
            void openSourcePreview({
              id: sourceId,
              sourceType: sourceDoc.sourceType,
              source: sourceDoc.source,
              onOpenPreview: setCortexPreviewId,
            }).catch((error) => {
              if (!isFileNotFoundError(error)) throw error;
              showToastNotification({
                type: 'error',
                title: 'File unavailable',
                description: 'This file is no longer available.',
              });
            }),
          deleteOption: configureDestructiveOptions([toDeleteCandidate(rowItem)]),
          onMakeGloballyAvailable: sourceDoc.availabilityScope === 'note' ? () => makeUserSourceGlobal(sourceId) : undefined,
        })}
      />
    </div>
  );
});
